import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IProject } from 'src/app/shared/models/i-project.modal';
import { ICompany } from 'src/app/shared/models/i-company.modal';
import { HttpService } from 'src/app/shared/services/http.service';
import { IProjectTestData } from 'src/app/shared/models/i-test-data.modal';
import { ReviewPayload, Sample } from 'src/app/shared/models/worksheet-checkbox.model';
import { IUser } from '../../models/iuser.modal';
import { PrecastWorksheet, PreCastWorksheetPatchDto } from '../models/precast-worksheet.modal';
import { WorksheetResponse } from '../models/worksheet-response.modal';
import { AsReceivedWorkSheetPatchDto, MoistureWorkSheetPatchDto, CompactionWorkSheetPatchDto, NativeSoilType, AsReceivedHistoryEvent, MoistureHistoryEvent, NativeSoilHistoryEvent, CompactionHistoryEvent } from '../models/native-soil-worksheet.modal';

type WorksheetType =
  | 'precast'
  | 'receivedSheet'
  | 'moistureWorksheets'
  | 'workSheet'
  | 'proctorWorksheets';

type AssignmentMode = 'single' | 'multiple';

type WorksheetUrlMap = {
  [key in AssignmentMode]: {
    [worksheet in WorksheetType]: string;
  };
};


@Injectable({
  providedIn: 'root',
})
export class WorksheetService {
  private worksheetUrlMap: WorksheetUrlMap = {
    single: {
      precast: 'precastws/assignlabtech',
      receivedSheet: 'nativesoilws/assignasreceivedrow',
      moistureWorksheets: 'nativesoilws/assignmoisturerow',
      workSheet: 'nativesoilws/assignnativerow',
      proctorWorksheets: 'nativesoilws/assignproctorrow',
    },
    multiple: {
      precast: 'precastws/AssignMultipleLabTechRows',
      receivedSheet: 'nativesoilws/AssignMultipleAsReceivedRows',
      moistureWorksheets: 'nativesoilws/AssignMultipleMoistureRows',
      workSheet: 'nativesoilws/AssignMultipleNativeRows',
      proctorWorksheets: 'nativesoilws/AssignMultipleProctorRows',
    },
  };

  constructor(private http: HttpClient, private httpService: HttpService) {}

  savePrecastWorksheetTable(payload: any[]): Observable<any> {
    return this.http.post<PreCastWorksheetPatchDto>(`${environment.baseUrl}precastws/savePreCastWorksheetTable`, payload);
  }

  saveAsReceivedWorksheetTable(payload: any[]): Observable<any> {
    return this.http.post<AsReceivedWorkSheetPatchDto>(`${environment.baseUrl}nativesoilws/saveAsReceivedWorksheetTable`, payload);
  }

  // Saves and assignments on the As-Received worksheet, newest first
  getAsReceivedHistory(projectId: string): Observable<AsReceivedHistoryEvent[]> {
    return this.httpService.get(`nativesoilws/getasreceivedhistory/${projectId}`);
  }

  // Saves and assignments on the Moisture Conditioning worksheet, newest first
  getMoistureHistory(projectId: string): Observable<MoistureHistoryEvent[]> {
    return this.httpService.get(`nativesoilws/getmoisturehistory/${projectId}`);
  }

  // Saves, assignments and TPA readings on the TR (native soil) worksheet, newest first
  getNativeSoilHistory(projectId: string): Observable<NativeSoilHistoryEvent[]> {
    return this.httpService.get(`nativesoilws/getnativesoilhistory/${projectId}`);
  }

  // Saves, completed status and assignments made from the Compaction worksheet, newest first
  getCompactionHistory(projectId: string): Observable<CompactionHistoryEvent[]> {
    return this.httpService.get(`nativesoilws/getcompactionhistory/${projectId}`);
  }
  
  saveMoistureConditioningWorksheetTable(payload: any[]): Observable<any> {
    return this.http.post<MoistureWorkSheetPatchDto>(`${environment.baseUrl}nativesoilws/saveMoistureConditioningWorksheetTable`, payload);
  }

  saveCompactionWorksheetTable(payload: any[]): Observable<any> {
    return this.http.post<CompactionWorkSheetPatchDto>(`${environment.baseUrl}nativesoilws/saveCompactionWorksheetTable`, payload);
  }

  saveNativeSoilWorksheetTable(payload: any[]): Observable<any> {
    return this.http.post<NativeSoilType>(`${environment.baseUrl}nativesoilws/saveNativeSoilWorksheetTable`, payload);
  }

  saveSingleProctorWorksheetTable(payload: any[]): Observable<any> {
    return this.http.post<NativeSoilType>(`${environment.baseUrl}nativesoilws/saveSingleProctorWorksheetTable`, payload);
  }

  public generatePrecastWorksheet(projectId: string): Observable<any> {
    return this.httpService.get(`precastws/generateworksheet/${projectId}`);
  }

  public generateNativeSoilWorksheet(projectId: string): Observable<any> {
    return this.httpService.get(`nativesoilws/generateworksheet/${projectId}`);
  }

  public fetchWorksheetbyId(projectId: string): Observable<PrecastWorksheet[]> {
    return this.httpService.get(`precastws/getworksheet/${projectId}`).pipe(map((w) => this.mapUser(w)));
  }

  public fetchWorksheetRowbyId(projectId: string): Observable<PrecastWorksheet[]> {
    return this.httpService.get(`precastws/getworksheetrow/${projectId}`);
  }

  public fetchNativeSoilWorksheet(projectId: string): Observable<WorksheetResponse> {
    return this.httpService.get(`worksheet/getnativesoilworksheets/${projectId}`);
  }

  public addWorksheet(payload: PrecastWorksheet): Observable<PrecastWorksheet> {
    return this.http.post<PrecastWorksheet>(`${environment.baseUrl}precastws/addworksheetrow`, payload);
  }

  public updateWorksheet(payload: PrecastWorksheet): Observable<PrecastWorksheet> {
    return this.http.post<PrecastWorksheet>(`${environment.baseUrl}precastws/updateworksheetrow`, payload);
  }

  public deleteWorksheet(worksheetId: string): Observable<object> {
    return this.http.delete(`${environment.baseUrl}precastws/deleteworksheetrow/${worksheetId}`);
  }

  public fetchLabTechs(): Observable<IUser[]> {
    return this.httpService.get('user/getlabtechs');
  }

  public fetchProjectById(projectId: string): Observable<IProject> {
    return this.httpService.get(`project/${projectId}`);
  }

  public fetchCompanyById(projectId: string): Observable<ICompany> {
    return this.httpService.get(`company/getbyprojectid/${projectId}`);
  }

  private buildWorksheetUrl(type: AssignmentMode, workSheetType: WorksheetType): string {
    return `${environment.baseUrl}${this.worksheetUrlMap[type][workSheetType]}`;
  }

  public assignSingleToLabtech(payload: any, workSheetType: WorksheetType): Observable<PrecastWorksheet> {
    const url = this.buildWorksheetUrl('single', workSheetType);
    return this.http.post<PrecastWorksheet>(url, payload);
  }

  public assignMultipleToLabtech(payload: any, workSheetType: WorksheetType): Observable<PrecastWorksheet> {
    const url = this.buildWorksheetUrl('multiple', workSheetType);
    return this.http.post<PrecastWorksheet>(url, payload);
  }
  
  public assignProject(payload: any, workSheetType: string) {
    let url = '';
    if (workSheetType === 'precast') {
      url = 'precastws/assignallworksheets';
    } else {
      url = 'nativesoilws/assignallworksheets';
    }
    return this.httpService.post(url, payload);
  }

  public updateProjectData(projectId: string | undefined, payload: IProjectTestData): Observable<IProject> {
    return this.httpService.patch(`project/updateprojecttestdata/${projectId}`, payload);
  }

  private mapUser(rows: any[]): any[] {
    rows.forEach((element) => {
      if (element.assignedToId) {
        element.Tech = element.assignedTo.userName;
      }
    });
    return rows;
  }

  public getPrecastViewSubmissionWorksheet(projectId: string): Observable<ReviewPayload> {
    return this.httpService.get(`dashboard/getpcmissinginfo/${projectId}`);
  }

  public addPrecastViewSubmissionWorksheet(projectId: string, payload: ReviewPayload): Observable<ReviewPayload> {
    return this.http.post<ReviewPayload>(
      `${environment.baseUrl}dashboard/addupdatepcmissinginfo/${projectId}`,
      payload,
    );
  }

  public getNativeSoilViewSubmissionWorksheet(projectId: string): Observable<ReviewPayload> {
    return this.httpService.get(`dashboard/getnsmissinginfo/${projectId}`);
  }

  public addNativeSoilViewSubmissionWorksheet(projectId: string, payload: ReviewPayload): Observable<ReviewPayload> {
    return this.http.post<ReviewPayload>(
      `${environment.baseUrl}dashboard/addupdatensmissinginfo/${projectId}`,
      payload,
    );
  }

  public sendEmailNotification(projectId: string, payload: string): Observable<string> {
    return this.http.post<string>(`${environment.baseUrl}notification/sendmissinginfomail/${projectId}`, payload);
  }

  public addPrecastSplitSubmissionWorksheet(projectId: string, payload: Sample[]): Observable<Sample[]> {
    return this.http.post<Sample[]>(
      `${environment.baseUrl}splitmerge/splitprecastproject/${projectId}`,
      payload,
    );
  }

  public addNativeSoilSplitSubmissionWorksheet(projectId: string, payload: Sample[]): Observable<Sample[]> {
    return this.http.post<Sample[]>(
      `${environment.baseUrl}splitmerge/splitnativeproject/${projectId}`,
      payload,
    );
  }

  public confirmMoistureContentNotification(projectId: string): Observable<boolean> {
    return this.httpService.post(`notification/confirmasreceivedmoisturecontent/${projectId}`);
  }

  public updateWorksheetSortingPrecast( data: any): Observable<any> {
    return this.httpService.post('precastws/updateworksheetsortingprecast', data);
  }

  public completeWorksheet(projectId: string): Observable<string> {
    return this.httpService.post(
      `precastws/completeworksheet/${projectId}`,
      null,
      { responseType: 'text' }
    );
  }
}
